import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  ConsentPreferences,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
  defaultConsent,
} from "../config/cookieConfig";
import { debugLog } from "../utils/logUtils";

declare global {
  function gtag(...args: unknown[]): void;
  function fbq(...args: unknown[]): void;
  interface Window {
    __darvin_pixels_initialized?: boolean;
  }
}

/**
 * Update Google Consent Mode state
 */
function updateGoogleConsent(analytics: boolean, marketing: boolean) {
  if (typeof gtag === "function") {
    gtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: marketing ? "granted" : "denied",
      ad_user_data: marketing ? "granted" : "denied",
      ad_personalization: marketing ? "granted" : "denied",
    });
  }
}

/**
 * Initialize Meta Pixel using official snippet from Meta Developer Portal
 * This uses Meta's exact IIFE which handles guard clauses and stub creation atomically
 * Source: https://developers.facebook.com/docs/meta-pixel/get-started
 */
function initMetaPixel(pixelId: string): void {
  // Meta's official pixel snippet - DO NOT MODIFY
  // The IIFE handles: guard clause, stub creation, property setup, and script loading all atomically
  (function (
    f: any,
    b: Document,
    e: string,
    v: string,
    n?: any,
    t?: HTMLScriptElement,
    s?: Element
  ) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e) as HTMLScriptElement;
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s?.parentNode?.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

  window.fbq("init", pixelId);
  window.fbq("track", "PageView");

  debugLog(`[DEBUG ${new Date().toISOString()}] Meta Pixel initialized with ID: ${pixelId}`);
  // Enable Meta's built-in debugging in DEV mode
  if (typeof window.fbq === "function") {
    window.fbq("set", "autoConfig", false, pixelId);
  }
}

function initMarketingPixels(): void {
  // Early return in dev mode (unless forced for testing)
  if (import.meta.env.DEV && !import.meta.env.VITE_FORCE_PIXELS) {
    debugLog(`[DEBUG ${new Date().toISOString()}] Skipping pixel init: dev mode without VITE_FORCE_PIXELS`);
    return;
  }

  // Simple guard - the official snippets have their own internal guards too
  if (window.__darvin_pixels_initialized) {
    debugLog(`[DEBUG ${new Date().toISOString()}] Pixels already initialized`);
    return;
  }
  window.__darvin_pixels_initialized = true;

  // Meta Pixel - uses official snippet with atomic initialization
  const metaPixelId = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
  if (metaPixelId) {
    initMetaPixel(metaPixelId);
  } else {
    debugLog(`[DEBUG ${new Date().toISOString()}] VITE_META_PIXEL_ID not configured`);
  }
}

interface ConsentContextType {
  consent: ConsentPreferences | null;
  hasConsented: boolean;
  showBanner: boolean;
  showPreferencesModal: boolean;
  updateConsent: (prefs: Partial<ConsentPreferences>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
  savePreferences: (analytics: boolean, marketing: boolean) => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

// Global consent state for non-React code (e.g., analyticsUtils.ts)
let globalConsent: ConsentPreferences | null = null;

/**
 * Get current consent preferences for non-React code
 */
export function getConsent(): ConsentPreferences | null {
  return globalConsent;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return ctx;
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentPreferences | null>(null);
  const [hasConsented, setHasConsented] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ConsentPreferences;
        // Check if consent version matches (re-consent if policy updated)
        if (parsed.consentVersion === CONSENT_VERSION) {
          setConsent(parsed);
          globalConsent = parsed;
          setHasConsented(true);
          setShowBanner(false);
          // Restore Google Consent Mode state
          updateGoogleConsent(parsed.analytics, parsed.marketing);
          // Initialize marketing pixels if previously consented
          if (parsed.marketing) {
            initMarketingPixels();
          }
        } else {
          // Policy version changed, need re-consent
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    } else {
      // No consent stored, show banner
      setShowBanner(true);
    }
    setInitialized(true);
  }, []);

  const saveToStorage = useCallback((prefs: ConsentPreferences) => {
    debugLog(`[DEBUG ${new Date().toISOString()}] Consent preferences saved: analytics=${prefs.analytics}, marketing=${prefs.marketing}`);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(prefs));
    setConsent(prefs);
    globalConsent = prefs;
    setHasConsented(true);
    setShowBanner(false);
    setShowPreferencesModal(false);
    // Sync with Google Consent Mode
    updateGoogleConsent(prefs.analytics, prefs.marketing);
    // Initialize marketing pixels if consented
    if (prefs.marketing) {
      initMarketingPixels();
    }
  }, []);

  const updateConsent = useCallback(
    (prefs: Partial<ConsentPreferences>) => {
      const updated: ConsentPreferences = {
        ...defaultConsent,
        ...consent,
        ...prefs,
        necessary: true, // Always true
        consentTimestamp: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
      };
      saveToStorage(updated);
    },
    [consent, saveToStorage]
  );

  const acceptAll = useCallback(() => {
    const allAccepted: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      consentTimestamp: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
    };
    saveToStorage(allAccepted);
  }, [saveToStorage]);

  const rejectAll = useCallback(() => {
    const allRejected: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      consentTimestamp: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
    };
    saveToStorage(allRejected);
  }, [saveToStorage]);

  const openPreferences = useCallback(() => {
    setShowPreferencesModal(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowPreferencesModal(false);
  }, []);

  const savePreferences = useCallback(
    (analytics: boolean, marketing: boolean) => {
      const updated: ConsentPreferences = {
        necessary: true,
        analytics,
        marketing,
        consentTimestamp: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
      };
      saveToStorage(updated);
    },
    [saveToStorage]
  );

  // Don't render children until initialized to prevent flash
  if (!initialized) {
    return null;
  }

  return (
    <ConsentContext.Provider
      value={{
        consent,
        hasConsented,
        showBanner,
        showPreferencesModal,
        updateConsent,
        acceptAll,
        rejectAll,
        openPreferences,
        closePreferences,
        savePreferences,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
}
